<template>
  <!-- Región de toasts -->
  <div class="ntf-region" role="status" aria-live="polite">
    <transition-group name="ntf-toast">
      <div v-for="t in toasts" :key="t.id" class="ntf-toast" :class="'ntf-toast--' + t.type">
        <span class="ntf-ic"><i :class="ICONS[t.type]"></i></span>
        <span class="ntf-msg">{{ t.message }}</span>
        <button v-if="t.action" type="button" class="ntf-act" @click="runAction(t)">{{ t.action }}</button>
        <button type="button" class="ntf-x" aria-label="Cerrar aviso" @click="dismissToast(t.id)">
          <i class="fas fa-times"></i>
        </button>
      </div>
    </transition-group>
  </div>

  <!-- Diálogo de confirmación -->
  <div v-if="confirmState.open" class="ntf-ov" @click.self="resolveConfirm(false)">
    <div class="ntf-dlg" role="alertdialog" aria-labelledby="ntf-dlg-title" aria-describedby="ntf-dlg-msg">
      <div class="ntf-dlg-ic" :class="{ 'ntf-dlg-ic--danger': confirmState.danger }">
        <i :class="confirmState.danger ? 'fas fa-trash' : 'fas fa-question'"></i>
      </div>
      <h3 id="ntf-dlg-title" class="ntf-dlg-title">{{ confirmState.title }}</h3>
      <p id="ntf-dlg-msg" class="ntf-dlg-msg">{{ confirmState.message }}</p>
      <div class="ntf-dlg-actions">
        <button ref="cancelBtn" type="button" class="ntf-btn ntf-btn--ghost" @click="resolveConfirm(false)">
          {{ confirmState.cancelText }}
        </button>
        <button type="button" class="ntf-btn" :class="confirmState.danger ? 'ntf-btn--danger' : 'ntf-btn--primary'"
          @click="resolveConfirm(true)">
          {{ confirmState.confirmText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useNotifyState } from '@/composables/useNotify'

const ICONS = {
  exito: 'fas fa-check',
  error: 'fas fa-times',
  aviso: 'fas fa-exclamation',
  info: 'fas fa-info',
}

export default {
  name: 'AppNotifications',
  setup() {
    const { toasts, dismissToast, confirmState, resolveConfirm } = useNotifyState()
    const cancelBtn = ref(null)

    function runAction(t) {
      dismissToast(t.id)
      if (t.onAction) t.onAction()
    }

    function onKey(e) {
      if (e.key === 'Escape' && confirmState.open) resolveConfirm(false)
    }

    onMounted(() => document.addEventListener('keydown', onKey))
    onBeforeUnmount(() => document.removeEventListener('keydown', onKey))

    // La acción destructiva nunca recibe el foco inicial
    watch(() => confirmState.open, open => {
      if (open) nextTick(() => { if (cancelBtn.value) cancelBtn.value.focus() })
    })

    return { toasts, dismissToast, confirmState, resolveConfirm, runAction, cancelBtn, ICONS }
  },
}
</script>

<style scoped>
/* ─── Región de toasts ─── */
.ntf-region { position: fixed; bottom: 24px; right: 24px; z-index: 3000; display: flex; flex-direction: column; align-items: flex-end; gap: 10px; pointer-events: none; }

/* ─── Toast: pastilla, mismo lenguaje que la toolbar de etiquetas ─── */
.ntf-toast { pointer-events: auto; display: flex; align-items: center; gap: 10px; max-width: min(420px, calc(100vw - 32px)); background: #fff; border: 1px solid var(--c-border, #e8e4df); border-radius: 999px; padding: 8px 12px 8px 8px; box-shadow: 0 6px 20px rgba(0,20,26,.16), 0 2px 6px rgba(0,20,26,.06); color: #22333a; font-family: var(--f-body, marine, 'Inter', sans-serif); font-size: .84rem; font-weight: 600; }
.ntf-ic { flex-shrink: 0; width: 30px; height: 30px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: .78rem; }
.ntf-msg { min-width: 0; }
.ntf-x { flex-shrink: 0; margin-left: 2px; width: 26px; height: 26px; border: none; border-radius: 50%; background: transparent; color: #7d8a90; font-size: .72rem; cursor: pointer; line-height: 1; display: inline-flex; align-items: center; justify-content: center; }
.ntf-x:hover { background: #f1f5f9; color: #22333a; }
.ntf-act { flex-shrink: 0; border: none; background: rgba(0,59,76,.08); color: var(--c-primary, #003b4c); font-family: inherit; font-size: .78rem; font-weight: 800; padding: 5px 12px; border-radius: 999px; cursor: pointer; }
.ntf-act:hover { background: rgba(0,59,76,.16); }
.ntf-x:focus-visible, .ntf-act:focus-visible, .ntf-btn:focus-visible { outline: 2px solid var(--c-primary, #003b4c); outline-offset: 2px; }

/* El color semántico vive en el icono y el borde, nunca en el fondo */
.ntf-toast--exito { border-color: rgba(22,163,74,.35); }
.ntf-toast--exito .ntf-ic { background: rgba(22,163,74,.13); color: #16a34a; }
.ntf-toast--error { border-color: rgba(220,38,38,.35); }
.ntf-toast--error .ntf-ic { background: rgba(220,38,38,.12); color: #dc2626; }
.ntf-toast--aviso { border-color: rgba(217,119,6,.38); }
.ntf-toast--aviso .ntf-ic { background: rgba(217,119,6,.13); color: #d97706; }
.ntf-toast--info { border-color: rgba(0,59,76,.28); }
.ntf-toast--info .ntf-ic { background: rgba(0,59,76,.1); color: var(--c-primary, #003b4c); }

/* Entrada/salida */
.ntf-toast-enter-active, .ntf-toast-leave-active, .ntf-toast-move { transition: opacity .22s ease, transform .22s ease; }
.ntf-toast-enter-from { opacity: 0; transform: translateY(12px); }
.ntf-toast-leave-to { opacity: 0; transform: translateY(8px); }

/* ─── Diálogo de confirmación ─── */
.ntf-ov { position: fixed; inset: 0; background: rgba(0,22,28,.5); display: flex; align-items: center; justify-content: center; padding: 20px; z-index: 2800; }
.ntf-dlg { background: #fff; color: #22333a; border-radius: 20px; max-width: 380px; width: 100%; padding: 24px; box-shadow: 0 24px 64px rgba(0,10,14,.35); font-family: var(--f-body, marine, 'Inter', sans-serif); animation: ntf-in .22s ease; }
.ntf-dlg-ic { width: 44px; height: 44px; border-radius: 50%; background: rgba(0,59,76,.1); color: var(--c-primary, #003b4c); display: flex; align-items: center; justify-content: center; font-size: .95rem; margin-bottom: 12px; }
.ntf-dlg-ic--danger { background: rgba(220,38,38,.12); color: #dc2626; }
.ntf-dlg-title { margin: 0 0 6px; font-size: 1.02rem; font-weight: 800; }
.ntf-dlg-msg { margin: 0 0 20px; font-size: .86rem; color: #5c6b71; line-height: 1.55; }
.ntf-dlg-actions { display: flex; justify-content: flex-end; gap: 10px; }
.ntf-btn { display: inline-flex; align-items: center; justify-content: center; gap: 7px; padding: 9px 20px; border-radius: 999px; font-family: inherit; font-size: .83rem; font-weight: 700; cursor: pointer; border: 2px solid transparent; transition: background .2s; white-space: nowrap; }
.ntf-btn--ghost { background: #fff; color: var(--c-primary, #003b4c); border-color: var(--c-primary, #003b4c); }
.ntf-btn--ghost:hover { background: rgba(0,59,76,.05); }
.ntf-btn--primary { background: var(--c-primary, #003b4c); color: #fff; border-color: var(--c-primary, #003b4c); }
.ntf-btn--primary:hover { background: #005570; border-color: #005570; }
.ntf-btn--danger { background: #dc2626; color: #fff; border-color: #dc2626; }
.ntf-btn--danger:hover { background: #b91c1c; border-color: #b91c1c; }

@keyframes ntf-in {
  from { opacity: 0; transform: translateY(12px); }
}

/* ─── Móvil: toasts centrados sobre el FAB; diálogo como hoja inferior ─── */
@media (max-width: 560px) {
  .ntf-region { left: 12px; right: 12px; bottom: 88px; align-items: center; }
  .ntf-ov { align-items: flex-end; padding: 0; }
  .ntf-dlg { max-width: none; border-radius: 20px 20px 0 0; padding-bottom: max(24px, env(safe-area-inset-bottom)); }
  .ntf-dlg-actions { flex-direction: column-reverse; }
  .ntf-dlg-actions .ntf-btn { width: 100%; }
}

@media (prefers-reduced-motion: reduce) {
  .ntf-toast-enter-active, .ntf-toast-leave-active, .ntf-toast-move { transition: none; }
  .ntf-dlg { animation: none; }
}

@media print {
  .ntf-region, .ntf-ov { display: none !important; }
}
</style>
